import { Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { ExtraInfoFields, iDeviceDetails, iDevicePing, iFullStoreState, Units } from "../../../../shared/interfaces";
import { clearReplayPoint, ReplaySpeed, setActiveDeviceId, setReplaySpeed } from "../../../../stores/reducers/replay/AC";
import { IReducerSelectedPoint } from "../../../../stores/reducers/selectedPoint";
import { useReplayControl } from "../../../gmap/ReplyContext";

import { ReactComponent as FastForward } from '../../../../assets/svg/fastForward.svg';
import { ReactComponent as FastRewind } from '../../../../assets/svg/fastRewind.svg';
import { ReactComponent as Pause } from '../../../../assets/svg/pause.svg';
import { ReactComponent as Play } from '../../../../assets/svg/play.svg';
import { ReactComponent as Reset } from '../../../../assets/svg/restart_alt_12w.svg';

import classNames from 'classnames';
import { getDeviceExtraInfo } from "../../../../shared/db/extra-info-db";
import { alertDotIcon, friendlySpeed } from '../../../../shared/helpers';
import CloseIcon from '../../../../assets/svg/close_cur.svg';
import { DashboardSize } from '../../../../stores/reducers/dashboardInfo';
import { getExtraInfoKeyByName } from "../../../../utils";
import styles from "./ReportTripDetailsModal.module.scss";
import { hideReportTrips } from '../../../../stores/reducers/devicesTripsPoints/AC';


const ReportTripDetailsModal = () => {
    const [makeModel, setMakeModel] = useState<string>('');

    const selectedPoint = useSelector<iFullStoreState, IReducerSelectedPoint>(state => state.selectedPoint);
    const currentReplayPoint = useSelector<iFullStoreState, iDevicePing | null>(state => state.replay?.point);

    const activePoint = currentReplayPoint || null;

    const selectedPointData = useSelector<iFullStoreState, iDevicePing | null>((state) => {
        if (currentReplayPoint || !selectedPoint) return null;
        return state.devicesTripsPoints.getIn([
            selectedPoint.deviceId,
            'tripsPoints',
            selectedPoint.tripId,
            selectedPoint.pointId
        ]) as iDevicePing || null;
    });

    const displayPoint = activePoint || selectedPointData;

    const [imagePath, setImagePath] = useState<string>('assets/incidents/gps_fixed.svg');

    const { startReplay, pauseReplay, resetReplay, navigateToNextPoint, navigateToPrevPoint, getCurrentPointInfo } = useReplayControl();

    const activeDeviceId = useSelector<iFullStoreState, string | null>(state => state.replay?.activeDeviceId);

    const replayPlayingStates = useSelector<iFullStoreState, { [deviceId: string]: boolean }>(state => state.replay?.isPlaying || {});
    const isPlaying = activeDeviceId ? (replayPlayingStates[activeDeviceId] || false) : false;

    const getDeviceIdForData = () => {
        // First priority: activeDeviceId from replay state
        if (activeDeviceId) {
            return activeDeviceId;
        }

        // Fallback: try to get deviceId from displayPoint
        if (displayPoint?.device) {
            return typeof displayPoint.device === 'string' ? displayPoint.device : (displayPoint.device as any).id;
        }

        // Fallback: try selectedPoint
        if (selectedPoint?.deviceId) {
            return selectedPoint.deviceId;
        }

        return null;
    };

    const deviceData = useSelector<iFullStoreState, iDeviceDetails | undefined>(
        (state) => {
            const deviceId = getDeviceIdForData();
            return deviceId ? state.devicesData.devicesDetails.get(deviceId) : undefined;
        }
    );

    const units = useSelector<iFullStoreState, Units>(
            state => state.devicesData.devicesDetails.getIn(
                [selectedPoint?.deviceId, 'units'],
                Units.miles,
            ) as Units);

    const getPointInfo = () => {
        const deviceId = getDeviceIdForData();
        if (!deviceId) return { currentIndex: 0, totalPoints: 0, hasNext: false, hasPrev: false };

        const pointInfo = getCurrentPointInfo(deviceId);
        return pointInfo || { currentIndex: 0, totalPoints: 0, hasNext: false, hasPrev: false };
    };

    const pointInfo = getPointInfo();

    const dispatch = useDispatch();

    const hasVisibleTrips = useSelector<iFullStoreState, boolean>(state => {
        return state.devicesTripsPoints.some(deviceTrips => {
            const reportTripsIds = deviceTrips.get('reportTripsIds');
            return reportTripsIds && reportTripsIds.some(show => show === true);
        });
    });

    useEffect(() => {
        if (!hasVisibleTrips && activeDeviceId) {
            dispatch(setActiveDeviceId(null));
        }
    }, [hasVisibleTrips, activeDeviceId, dispatch]);

    useEffect(() => {
        setImagePath('assets/incidents/gps_fixed.svg');
        getImagePath();
        formatMakeModel();
    }, [displayPoint?.pointId]);

    const getImagePath = async () => {
        const event = Object.keys(displayPoint?.alertActivity || {})[0] || '';

        if (event !== '') {
            const path = await alertDotIcon(displayPoint.alertActivity);
            if (typeof path === 'string') {
                setImagePath(path);
            }
        }
    };

    const formatMakeModel = async () => {
        const deviceId = getDeviceIdForData();
        if (!deviceId) return;

        try {
            const keys = await Promise.all([
                getExtraInfoKeyByName(ExtraInfoFields.YEAR),
                getExtraInfoKeyByName(ExtraInfoFields.MAKE),
                getExtraInfoKeyByName(ExtraInfoFields.MODEL)
            ]);

            const values = await Promise.all(
                keys.map(async (key) => {
                    const extraInfo = await getDeviceExtraInfo(deviceId, key);
                    return extraInfo;
                })
            );

            const formatted = values
                .map((item) => {
                    if (typeof item === 'string') {
                        return item;
                    }

                    if (item && typeof item === 'object' && typeof item.text === 'string') {
                        return item.text;
                    }
                    return null;
                })
                .filter((e): e is string => e !== null && e.trim() !== '')
                .join(' ');

            setMakeModel(formatted.trim());
        } catch (error) {
            setMakeModel('');
        }
    };

    const getDeviceId = () => {
        if (activeDeviceId) {
          return activeDeviceId;
        }

        if (displayPoint?.device) {
          const deviceId = typeof displayPoint.device === 'string' ? displayPoint.device : (displayPoint.device as any).id;

          return deviceId;
        }

        if (selectedPoint?.deviceId) {
          return selectedPoint.deviceId;
        }

        return null;
    };

    const handlePlayPause = () => {
        const deviceId = getDeviceId();

        if (deviceId) {
            if (isPlaying) {
                pauseReplay(deviceId);
            } else {
                startReplay(deviceId);
            }
        }
    };

    const handleReset = () => {
        const deviceId = getDeviceId();

        if (deviceId) {
            resetReplay(deviceId);
            dispatch(clearReplayPoint());
        }
    };

    const handlePrevPoint = async () => {
        const deviceId = getDeviceIdForData();

        if (!deviceId) {
            return;
        }

        navigateToPrevPoint(deviceId);
    };

    const handleNextPoint = async () => {
        const deviceId = getDeviceIdForData();

        if (!deviceId) {
            return;
        }

        navigateToNextPoint(deviceId);
    };

    // Helper function to format address like in PointDetails
    const formatAddress = (address) => {
        const addressEntries: string[] = [];

        if (address) {
          addressEntries.push(address.street?.replace(/\S*\+[^ ]*/g, '') || '');
        }

        if (address?.city) addressEntries.push(address.city);
        if (address?.state) addressEntries.push(address.state);
        if (address?.zip) addressEntries.push(address.zip);

        return addressEntries.map(entry => entry.replace(/US/gi, '')).filter(entry => entry !== '').join(', ');
    };

    const getDeviceName = () => {
        if (deviceData?.name) {
          return deviceData.name;
        }

        if (displayPoint?.deviceName) {
          return displayPoint.deviceName;
        }

        if (selectedPoint?.deviceId) {
          return selectedPoint.deviceId;
        }

        return "Trip";
    };

    const isEvent = displayPoint?.alertActivity && Object.keys(displayPoint.alertActivity).length > 0;

    if (!activeDeviceId) {
        return null;
    }

    return (
        <section className={styles.reportTripDetailsModal}>
            {/* Header */}
            <header className={styles.header}>
                {/* Header Row */}
                <div className={styles.headerRow}>
                    {/* Title */}
                    <div className={styles.titleContainer}>
                        <p>Selected Trip Point:</p>

                        {pointInfo.totalPoints > 1 && (
                            <span className={styles.tripCounter}>
                                Point {pointInfo.currentIndex + 1} of {pointInfo.totalPoints}
                            </span>
                        )}
                    </div>

                    {/* Close Button */}
                    <button
                      onClick={() => dispatch(hideReportTrips())}
                    >
                        <img
                          className={styles.closeIcon}
                          src={CloseIcon}
                          alt="Close icon"
                        />
                    </button>
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    <Tooltip title={pointInfo.hasPrev ? "prev point" : "no previous point"}>
                        <button onClick={handlePrevPoint} disabled={!pointInfo.hasPrev}>
                            <FastRewind />
                        </button>
                    </Tooltip>

                    <Tooltip title={isPlaying ? "pause" : "play"}>
                        <button onClick={handlePlayPause}>
                            {isPlaying ? <Pause /> : <Play />}
                        </button>
                    </Tooltip>

                    <Tooltip title="reset">
                        <button onClick={handleReset}>
                            <Reset />
                        </button>
                    </Tooltip>

                    <Tooltip title={pointInfo.hasNext ? "next point" : "no next point"}>
                        <button onClick={handleNextPoint} disabled={!pointInfo.hasNext}>
                            <FastForward />
                        </button>
                    </Tooltip>
                </div>

                {/* Slider */}
                <div className={styles.sliderRow}>
                    <SpeedSlider />
                </div>
            </header>

            {/* Trip Point Details Section - moved from TripPointDetails */}
            <section className={styles.tripPointDetails}>
                   <div className={styles.tripPointDetailsHeader}>
                        <img
                            className={styles.tripPointDetailsImage}
                            src={imagePath}
                            alt="Device Icon"
                        />

                        <div className={styles.tripPointDetailsData}>
                            <div className={styles.tripPointDetailsInfoContainer}>
                                <span className={styles.tripPointDetailsName}>
                                    {getDeviceName()}
                                </span>
                            </div>

                            {isEvent ? (
                                <span className={styles.tripPointDetailsMessage}>
                                    Event: <span style={{ fontWeight: 500 }}>{displayPoint.msg}</span>
                                </span>
                            ) : (
                                <span className={styles.tripPointDetailsMessage}>
                                    Event: <span style={{ fontWeight: 500 }}>{displayPoint?.msg || "No event"}</span>
                                </span>
                            )}
                        </div>
                    </div>

                <div className={styles.tripPointDetailsBorder}></div>

                <div className={styles.tripPointDetailsInfo}>
                    <div className={styles.infoEntry}>
                        <span className={styles.label}>Current Location:</span>

                        <span className={styles.infoData}>
                            {formatAddress(displayPoint?.address) || (displayPoint as any)?.endAddress || "-"}
                        </span>
                    </div>

                    <div className={styles.infoEntry}>
                        <span className={styles.label}>Date & Time:</span>

                        <span className={styles.infoData}>
                            {displayPoint?.time?.format ?
                                displayPoint.time.format("MM/DD/YY h:mm A") :
                                displayPoint?.endDate?.format ?
                                    displayPoint.endDate.format("MM/DD/YY h:mm A") :
                                    "-"
                            }
                        </span>
                    </div>

                    <div className={styles.infoEntry}>
                        <span className={styles.label}>Speed:</span>

                        <span className={styles.infoData}>
                            {displayPoint?.speed !== undefined ?
                                friendlySpeed(displayPoint.speed, units) :
                                "-"
                            }
                        </span>
                    </div>

                    {makeModel !== '' && (
                        <div className={styles.infoEntry}>
                            <span className={styles.label}>Vehicle:</span>

                            <span className={styles.infoData}>{makeModel}</span>
                        </div>
                    )}
                    {displayPoint?.posted_speed && (
                        <div className={styles.infoEntry}>
                            <span className={styles.label}>Posted Speed:</span>

                            <span className={styles.infoData}>{displayPoint.posted_speed}</span>
                        </div>
                    )}
                    <div className={styles.infoEntry}>
                        <span className={styles.label}>Fuel:</span>

                        <span className={styles.infoData}>
                            {displayPoint?.fuel !== undefined ?
                                `${displayPoint.fuel}%` :
                                "-"
                            }
                        </span>
                    </div>
                </div>
            </section>
        </section>
    )
}

export default ReportTripDetailsModal;

const SPEED_VALUES = [ReplaySpeed.SLOW, ReplaySpeed.NORMAL, ReplaySpeed.FAST, ReplaySpeed.VERY_FAST];

const SpeedSlider = () => {
    const dispatch = useDispatch();
    const speed = useSelector<iFullStoreState, ReplaySpeed>(state => state.replay?.speed || ReplaySpeed.NORMAL);
    const size = useSelector<iFullStoreState, DashboardSize>((s) => s.dashboardInfo.size);
    const useColumnLayout = size === DashboardSize.OPEN || size === DashboardSize.OPEN_SMALL;

    const min = ReplaySpeed.SLOW;
    const max = ReplaySpeed.VERY_FAST;

    const percentage = ((speed - min) / (max - min)) * 100;

    const handleSpeedChange = (newSpeed: number) => {
        const replaySpeed = newSpeed as ReplaySpeed;
        dispatch(setReplaySpeed(replaySpeed));
    };

    return (
        <div className={styles.container}>
            <div className={classNames(styles.speedControlRow, {[styles.speedControlColumn]: useColumnLayout})}>
                <div className={styles.speedTitle}>Set Playback Speed:</div>

                <div className={styles.speedSliderWrapper}>
                    <Tooltip
                        title={`${speed}x`}
                        placement="top"
                    >
                        <input
                            type="range"
                            min={min}
                            max={max}
                            step={0.5}
                            value={speed}
                            onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                            style={{ background: `linear-gradient(to right, #FF7C02 0%, #FF7C02 ${percentage}%, #ccc ${percentage}%, #ccc 100%)` }}
                            list="tickmarks"
                            className={styles.slider}
                        />
                    </Tooltip>

                    <datalist id="tickmarks">
                        {SPEED_VALUES.map((val) => (
                            <option key={val} value={val} />
                        ))}
                    </datalist>

                    <div className={styles.labels}>
                        {SPEED_VALUES.map((val) => (
                            <span key={val}>{val}</span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
